import React from 'react';

interface TimelineEvent {
  year: string;
  title: string;
  description: string;
}

const Timeline: React.FC = () => {
  const events: TimelineEvent[] = [
    {
      year: '1950',
      title: 'Born in Tramore',
      description: 'Born on March 15th in Tramore, Co. Waterford, to Patrick and Siobhan Smith.',
    },
    {
      year: '1968',
      title: 'Academic Excellence',
      description: 'Graduated as top student from Mount Sion Secondary School, earning a scholarship to University College Dublin.',
    },
    {
      year: '1972',
      title: 'University Success',
      description: 'Earned First Class Honours degree in Engineering from UCD, setting the foundation for his future in education.',
    },
    {
      year: '1975',
      title: 'Marriage to Mary',
      description: 'Met and married Mary O\'Brien in a beautiful ceremony at the Holy Cross Church in Tramore.',
    },
    {
      year: '1978',
      title: 'Career Beginnings',
      description: 'Joined Waterford Regional Technical College (now SETU) as a lecturer in Engineering.',
    },
    {
      year: '1980',
      title: 'Growing Family',
      description: 'Welcomed son John Jr., followed by daughter Niamh in 1982, completing their family.',
    },
    {
      year: '1995',
      title: 'Academic Achievement',
      description: 'Appointed Head of Engineering Department at SETU, pioneering new teaching methodologies.',
    },
    {
      year: '2010',
      title: 'New Chapter',
      description: 'Welcomed first grandchild, beginning a cherished role as grandfather to eventually five grandchildren.',
    },
    {
      year: '2015',
      title: 'Legacy of Learning',
      description: 'Established the Smith Engineering Scholarship at SETU for promising local students.',
    },
    {
      year: '2024',
      title: 'Final Chapter',
      description: 'Passed peacefully at home in Tramore, leaving behind a legacy of education, family values, and community spirit.',
    },
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Section Title */}
      <div className="text-center mb-16">
        <h2 className="font-ornate text-4xl md:text-5xl text-burgundy-900 mb-4">Life Journey</h2>
        <div className="flex items-center justify-center gap-4">
          <div className="w-16 h-px bg-gold-400/30"></div>
          <div className="text-gold-400 text-xl">✤</div>
          <div className="w-16 h-px bg-gold-400/30"></div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="relative">
        {/* Decorative corners */}
        <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-gold-400/20"></div>
        <div className="absolute top-0 right-0 w-16 h-16 border-t-2 border-r-2 border-gold-400/20"></div>
        <div className="absolute bottom-0 left-0 w-16 h-16 border-b-2 border-l-2 border-gold-400/20"></div>
        <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-gold-400/20"></div>

        {/* Content with padding for corners */}
        <div className="px-8 py-12">
          {/* Introduction */}
          <div className="mb-12 text-center">
            <p className="font-serif text-xl text-burgundy-800 italic mb-6">
              A chronological journey through the remarkable life of John David Smith, from his early days in Tramore to his lasting legacy.
            </p>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-[7.5rem] top-0 bottom-0 w-[2px] bg-gold-400/10">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-gold-400/20 to-transparent"></div>
            </div>

            {/* Events */}
            <div className="space-y-24">
              {events.map((event, index) => (
                <div 
                  key={index}
                  className="relative flex group items-start"
                >
                  {/* Year Label */}
                  <div className="w-24 pt-1 pr-4 text-right">
                    <span className="font-ornate text-xl text-burgundy-800 opacity-80 group-hover:opacity-100 transition-opacity duration-300">
                      {event.year}
                    </span>
                  </div>

                  {/* Timeline Point */}
                  <div className="relative flex items-center justify-center w-12">
                    <div className="absolute w-8 h-8 rounded-full border-2 border-gold-400/20 group-hover:border-gold-400/40 transition-all duration-500"></div>
                    <div className="w-3 h-3 rounded-full bg-gold-400/40 shadow-sm transform transition-all duration-500 group-hover:scale-125"></div>
                  </div>

                  {/* Content Card */}
                  <div className="flex-1 pt-0 pl-8">
                    <div className="relative transform transition-all duration-500 group-hover:-translate-y-1">
                      {/* Connector Line */}
                      <div className="absolute left-[-1.75rem] top-[0.6rem] w-6 h-[1px] bg-gold-400/20 group-hover:bg-gold-400/40 transition-colors duration-300"></div>
                      
                      <div className="relative bg-parchment-50/95 rounded-sm p-6 border border-gold-400/10 shadow-md group-hover:shadow-lg transition-all duration-300">
                        {/* Decorative corners for each card */}
                        <div className="absolute top-0 left-0 w-8 h-8 border-t border-l border-gold-400/20"></div>
                        <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-gold-400/20"></div>
                        <div className="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-gold-400/20"></div>
                        <div className="absolute bottom-0 right-0 w-8 h-8 border-b border-r border-gold-400/20"></div>
                        
                        <h3 className="font-ornate text-2xl text-burgundy-900 mb-3">
                          {event.title}
                        </h3>
                        <p className="font-serif text-burgundy-800 leading-relaxed">
                          {event.description}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Timeline end decoration */}
            <div className="absolute left-[7.5rem] bottom-0 w-[2px] h-16 bg-gradient-to-b from-gold-400/20 to-transparent"></div>
          </div>

          {/* Decorative Footer */}
          <div className="mt-16 text-center">
            <div className="flex items-center justify-center gap-4">
              <div className="w-24 h-px bg-gold-400/30"></div>
              <div className="text-gold-400/80 text-xl">❧</div>
              <div className="w-24 h-px bg-gold-400/30"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Timeline; 